import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Send, Upload, Check, Search, X } from "lucide-react";

const STEPS = ["Client", "Invoice & PO", "Transporter & LR", "Upload Docs", "Recipients", "Preview & Send"];
const DOC_TYPES = ["Tax Invoice", "LR / Lorry Receipt", "Client PO", "Packing List", "E-Way Bill", "Delivery Challan", "Test Certificate", "POD", "Other"];

export default function NewDispatch() {
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [contacts, setContacts] = useState([]);
  const nav = useNavigate();

  const [form, setForm] = useState({
    client_id: "", invoice_number: "", invoice_date: "", invoice_amount: "",
    client_po_number: "", po_date: "", delivery_location: "",
    transporter_id: "", dispatch_date: new Date().toISOString().slice(0, 10),
    lr_number: "", transport_amount: 0,
    loading_cost: 0, unloading_cost: 0, packing_cost: 0, insurance_cost: 0, other_charges: 0,
    expected_delivery: "", delivery_status: "dispatched", remarks: "",
  });
  const [dispatch, setDispatch] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState({});
  const [channels, setChannels] = useState({ whatsapp: true, email: true });
  const [attachDocs, setAttachDocs] = useState({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [clientQuery, setClientQuery] = useState("");
  const [abOpen, setAbOpen] = useState(false);
  const [abLoading, setAbLoading] = useState(false);
  const [abInvoices, setAbInvoices] = useState([]);
  const [abError, setAbError] = useState("");
  const [abConfigured, setAbConfigured] = useState(true);

  useEffect(() => {
    (async () => {
      const [c, t] = await Promise.all([api.get("/clients"), api.get("/transporters")]);
      setClients(c.data); setTransporters(t.data);
    })();
  }, []);

  useEffect(() => {
    if (!form.client_id) return;
    api.get(`/contacts?client_id=${form.client_id}`).then(({ data }) => {
      setContacts(data);
      const sel = {}; data.filter((x) => x.is_active !== false).forEach((c) => sel[c.id] = true);
      setSelectedContacts(sel);
    });
  }, [form.client_id]);

  const selectedClient = clients.find((c) => c.id === form.client_id);
  const selectedTransporter = transporters.find((t) => t.id === form.transporter_id);

  const openAlignbooks = async () => {
    setAbOpen(true); setAbLoading(true); setAbError(""); setAbInvoices([]);
    try {
      const { data } = await api.get("/integrations/alignbooks/invoices");
      setAbConfigured(!!data.configured);
      if (data.error) setAbError(data.error);
      setAbInvoices(data.invoices || []);
    } catch (e) { setAbError(formatApiError(e)); }
    finally { setAbLoading(false); }
  };

  const pickAlignbooksInvoice = (inv) => {
    const match = clients.find((c) => (c.name || "").toLowerCase().trim() === (inv.client_name || "").toLowerCase().trim());
    setForm((f) => ({
      ...f,
      client_id: match ? match.id : f.client_id,
      invoice_number: inv.invoice_number || f.invoice_number,
      invoice_date: (inv.invoice_date || "").slice(0, 10) || f.invoice_date,
      invoice_amount: inv.invoice_amount != null ? String(inv.invoice_amount) : f.invoice_amount,
      client_po_number: inv.client_po_number || f.client_po_number,
      delivery_location: inv.branch || f.delivery_location,
    }));
    setAbOpen(false);
    toast.success(`Prefilled from AlignBooks · ${inv.invoice_number || "invoice"}`);
    if (!match && inv.client_name) toast.info(`No matching client found for "${inv.client_name}" — please pick manually`);
  };

  const canNext = useMemo(() => {
    if (step === 0) return !!form.client_id;
    if (step === 1) return form.invoice_number && form.invoice_date && form.invoice_amount;
    if (step === 2) return form.transporter_id && form.lr_number && form.dispatch_date;
    if (step === 3) return true;
    if (step === 4) return Object.values(selectedContacts).some(Boolean);
    return true;
  }, [step, form, selectedContacts]);

  const createDispatch = async () => {
    try {
      const payload = { ...form, invoice_amount: Number(form.invoice_amount || 0), transport_amount: Number(form.transport_amount || 0) };
      ["loading_cost", "unloading_cost", "packing_cost", "insurance_cost", "other_charges"].forEach((k) => payload[k] = Number(payload[k] || 0));
      const { data } = await api.post("/dispatches", payload);
      setDispatch(data);
      toast.success(`Dispatch ${data.dispatch_code} created`);
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  const uploadDoc = async (file, doc_type) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("doc_type", doc_type); fd.append("is_client_visible", "true");
    const { data } = await api.post(`/dispatches/${dispatch.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setDocuments((prev) => [...prev, data]);
    setAttachDocs((prev) => ({ ...prev, [data.id]: true }));
    toast.success(`Uploaded ${data.original_filename}`);
  };

  const goNext = async () => {
    if (step === 2 && !dispatch) {
      try { await createDispatch(); } catch { return; }
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const send = async () => {
    setSending(true);
    try {
      const contact_ids = Object.entries(selectedContacts).filter(([, v]) => v).map(([k]) => k);
      const channelList = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
      const attach_document_ids = Object.entries(attachDocs).filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post("/notify/send", { dispatch_id: dispatch.id, contact_ids, channels: channelList, attach_document_ids });
      setResult(data);
      toast.success(`Sent: ${data.summary.sent} · Failed: ${data.summary.failed}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="page-new-dispatch" className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Operations</div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">New Dispatch</h1>
      </div>

      {/* Stepper */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-2 overflow-x-auto">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 shrink-0">
            <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-[var(--navy)] text-white" : "bg-slate-200 text-slate-600"}`}>
              {i < step ? <Check size={13} /> : i + 1}
            </div>
            <div className={`text-xs font-semibold ${i === step ? "text-slate-900" : "text-slate-500"}`}>{s}</div>
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-slate-200 mx-2" />}
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        {step === 0 && (
          <div>
            <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Client*</label>

            {/* Searchable client picker */}
            <div className="relative mt-1">
              <div className="flex items-center gap-2 px-3 py-2.5 border border-slate-300 rounded-md focus-within:border-slate-500 bg-white">
                <Search size={14} className="text-slate-400" />
                <input
                  data-testid="wiz-client-search"
                  autoFocus
                  type="text"
                  placeholder={selectedClient ? `${selectedClient.code} · ${selectedClient.name}` : "Search client by name, code, city or GST…"}
                  value={clientQuery}
                  onChange={(e) => { setClientQuery(e.target.value); if (form.client_id) setForm({ ...form, client_id: "" }); }}
                  className="flex-1 outline-none text-sm bg-transparent"
                />
                {(clientQuery || selectedClient) && (
                  <button
                    type="button"
                    data-testid="wiz-client-clear"
                    onClick={() => { setClientQuery(""); setForm({ ...form, client_id: "" }); }}
                    className="text-slate-400 hover:text-slate-700"
                    aria-label="Clear"
                  ><X size={14} /></button>
                )}
              </div>

              {(clientQuery || !selectedClient) && (
                <div data-testid="wiz-client-list" className="mt-1 max-h-72 overflow-y-auto border border-slate-200 rounded-md bg-white shadow-sm">
                  {(() => {
                    const q = clientQuery.trim().toLowerCase();
                    const filtered = q
                      ? clients.filter((c) =>
                          [c.name, c.code, c.city, c.state, c.gst_number]
                            .filter(Boolean)
                            .some((v) => String(v).toLowerCase().includes(q))
                        )
                      : clients;
                    if (filtered.length === 0) {
                      return <div className="px-3 py-3 text-sm text-slate-500">No clients match “{clientQuery}”. Add one in <b>Clients</b>.</div>;
                    }
                    return filtered.slice(0, 100).map((c) => (
                      <button
                        type="button"
                        key={c.id}
                        data-testid={`wiz-client-option-${c.code}`}
                        onClick={() => { setForm({ ...form, client_id: c.id }); setClientQuery(""); }}
                        className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-sky-50 flex items-center gap-3 ${form.client_id === c.id ? "bg-sky-50" : ""}`}
                      >
                        <span className="text-[11px] font-mono bg-slate-100 rounded px-1.5 py-0.5 text-slate-700">{c.code}</span>
                        <span className="flex-1">
                          <span className="text-sm font-medium text-slate-900">{c.name}</span>
                          <span className="block text-xs text-slate-500">{[c.city, c.state].filter(Boolean).join(", ")}{c.gst_number ? ` · GST ${c.gst_number}` : ""}</span>
                        </span>
                        {form.client_id === c.id && <Check size={14} className="text-emerald-600" />}
                      </button>
                    ));
                  })()}
                </div>
              )}
            </div>

            {selectedClient && !clientQuery && (
              <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-md text-sm">
                <div className="font-semibold">{selectedClient.name}</div>
                <div className="text-slate-500 text-xs mt-1">{selectedClient.city}, {selectedClient.state} · GST: {selectedClient.gst_number || "N/A"}</div>
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500">Enter invoice details manually, or pull from AlignBooks to skip typing.</div>
              <button
                type="button"
                data-testid="btn-pull-alignbooks"
                onClick={openAlignbooks}
                className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 border border-sky-300 bg-sky-50 text-sky-800 rounded-md hover:bg-sky-100"
              >
                <Upload size={13} /> Pull from AlignBooks
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["invoice_number", "SRAD Invoice Number*", true, "text"],
                ["invoice_date", "Invoice Date*", true, "date"],
                ["invoice_amount", "Invoice Amount (Rs.)*", true, "number"],
                ["client_po_number", "Client PO Number"],
                ["po_date", "PO Date", false, "date"],
                ["delivery_location", "Delivery Location"],
              ].map(([k, l, req, type = "text"]) => (
                <div key={k}>
                  <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">{l}</label>
                  <input data-testid={`wiz-${k}`} type={type} required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Transporter*</label>
              <select data-testid="wiz-transporter" value={form.transporter_id} onChange={(e) => setForm({ ...form, transporter_id: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm">
                <option value="">Select transporter…</option>
                {transporters.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
              </select>
            </div>
            {[
              ["dispatch_date", "Dispatch Date*", true, "date"],
              ["lr_number", "LR / Tracking Number*", true],
              ["transport_amount", "Transport Amount (Rs.)", false, "number"],
              ["expected_delivery", "Expected Delivery", false, "date"],
              ["loading_cost", "Loading Cost", false, "number"],
              ["unloading_cost", "Unloading Cost", false, "number"],
              ["packing_cost", "Packing Cost", false, "number"],
              ["insurance_cost", "Insurance Cost", false, "number"],
              ["other_charges", "Other Charges", false, "number"],
            ].map(([k, l, req, type = "text"]) => (
              <div key={k}>
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">{l}</label>
                <input data-testid={`wiz-${k}`} type={type} required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
            ))}
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="text-sm text-slate-600 mb-3">Dispatch <span className="font-mono font-semibold">{dispatch?.dispatch_code}</span> created. Upload Invoice + LR + additional docs.</div>
            <div className="grid grid-cols-2 gap-3">
              {DOC_TYPES.map((dt) => (
                <label key={dt} className="border border-dashed border-slate-300 hover:border-slate-500 rounded-md p-4 cursor-pointer flex items-center gap-3 transition-colors">
                  <Upload size={18} className="text-slate-500" />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-slate-800">{dt}</div>
                    <div className="text-xs text-slate-500">Click to upload (PDF/JPG/PNG)</div>
                  </div>
                  <input data-testid={`wiz-upload-${dt}`} type="file" className="hidden" accept="application/pdf,image/*" onChange={(e) => e.target.files?.[0] && uploadDoc(e.target.files[0], dt)} />
                </label>
              ))}
            </div>
            <div className="mt-5">
              <div className="text-xs uppercase font-semibold tracking-widest text-slate-500 mb-2">Uploaded</div>
              <div className="space-y-1">
                {documents.map((d) => (
                  <div key={d.id} className="text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex items-center justify-between">
                    <div><span className="text-xs bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5 mr-2 font-semibold">{d.doc_type}</span>{d.original_filename}</div>
                    <div className="text-xs text-slate-500">{Math.round(d.size / 1024)} KB</div>
                  </div>
                ))}
                {documents.length === 0 && <div className="text-sm text-slate-500">No documents uploaded yet.</div>}
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <button onClick={() => { const s = {}; contacts.forEach((c) => s[c.id] = true); setSelectedContacts(s); }} className="text-xs px-3 py-1.5 border border-slate-300 rounded-full">Select All</button>
              <button onClick={() => { const s = {}; contacts.filter((c) => c.whatsapp).forEach((c) => s[c.id] = true); setSelectedContacts(s); }} className="text-xs px-3 py-1.5 border border-slate-300 rounded-full">WhatsApp only</button>
              <button onClick={() => { const s = {}; contacts.filter((c) => c.email).forEach((c) => s[c.id] = true); setSelectedContacts(s); }} className="text-xs px-3 py-1.5 border border-slate-300 rounded-full">Email only</button>
              <button onClick={() => setSelectedContacts({})} className="text-xs px-3 py-1.5 border border-slate-300 rounded-full">Clear</button>
              <div className="ml-auto flex items-center gap-4 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={channels.whatsapp} onChange={(e) => setChannels({ ...channels, whatsapp: e.target.checked })} data-testid="ch-whatsapp" /> WhatsApp</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={channels.email} onChange={(e) => setChannels({ ...channels, email: e.target.checked })} data-testid="ch-email" /> Email</label>
              </div>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {contacts.map((c) => (
                <label key={c.id} data-testid={`recipient-${c.id}`} className="flex items-center gap-3 p-3 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={!!selectedContacts[c.id]} onChange={(e) => setSelectedContacts({ ...selectedContacts, [c.id]: e.target.checked })} />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{c.name} <span className="text-xs text-slate-500 font-normal">· {c.department}</span></div>
                    <div className="text-xs text-slate-500">{c.whatsapp && <span>WA: {c.whatsapp}</span>} {c.email && <span className="ml-3">Email: {c.email}</span>}</div>
                  </div>
                  <span className="text-xs bg-slate-100 rounded-full px-2 py-0.5">{c.preferred}</span>
                </label>
              ))}
              {contacts.length === 0 && <div className="text-sm text-slate-500">No contacts. Add contacts to this client first.</div>}
            </div>
            <div className="mt-5">
              <div className="text-xs uppercase font-semibold tracking-widest text-slate-500 mb-2">Attachments</div>
              <div className="grid grid-cols-2 gap-2">
                {documents.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 text-sm">
                    <input type="checkbox" checked={!!attachDocs[d.id]} onChange={(e) => setAttachDocs({ ...attachDocs, [d.id]: e.target.checked })} />
                    <span className="font-semibold text-xs">{d.doc_type}</span> <span className="text-slate-500 truncate">{d.original_filename}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            {!result ? (
              <div className="space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm">
                  <div className="font-semibold mb-2">Ready to send</div>
                  <div>Dispatch: <span className="font-mono">{dispatch?.dispatch_code}</span></div>
                  <div>Client: {selectedClient?.name}</div>
                  <div>Transporter: {selectedTransporter?.name}</div>
                  <div>Recipients: {Object.values(selectedContacts).filter(Boolean).length} contacts</div>
                  <div>Channels: {Object.entries(channels).filter(([, v]) => v).map(([k]) => k).join(", ")}</div>
                  <div>Attachments: {Object.values(attachDocs).filter(Boolean).length} docs</div>
                </div>
                <div className="flex gap-3">
                  <button data-testid="btn-send-notifications" disabled={sending} onClick={send} className="flex-1 bg-[var(--amber)] hover:bg-amber-600 text-white font-semibold text-sm py-2.5 rounded-md flex items-center justify-center gap-2 disabled:opacity-60">
                    <Send size={15} /> {sending ? "Sending…" : "Send Notifications"}
                  </button>
                  <button onClick={() => nav(`/dispatches/${dispatch.id}`)} className="px-4 py-2.5 border border-slate-300 rounded-md text-sm">Skip & Open Dispatch</button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-sm">
                  <div className="font-heading font-bold text-emerald-800 text-base">Dispatch {dispatch.dispatch_code} — Notifications Complete</div>
                  <div className="text-emerald-700 mt-1">Sent: {result.summary.sent} · Failed: {result.summary.failed} · Total: {result.summary.total}</div>
                </div>
                <div className="border border-slate-200 rounded-md max-h-72 overflow-y-auto">
                  {result.results.map((r) => (
                    <div key={r.id} className="text-sm px-4 py-2 border-b border-slate-100 flex items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold uppercase mr-2 px-2 py-0.5 rounded-full bg-slate-100">{r.channel}</span>
                        <span className="font-medium">{r.contact_name}</span>
                        <span className="text-slate-500 ml-2 text-xs">{r.to_address}</span>
                      </div>
                      <span className={`text-xs font-semibold ${r.status === "sent" ? "text-emerald-700" : "text-rose-700"}`}>{r.status}{r.error ? ` · ${r.error.slice(0, 60)}` : ""}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 pt-2">
                  <button data-testid="btn-view-dispatch" onClick={() => nav(`/dispatches/${dispatch.id}`)} className="bg-[var(--navy)] text-white text-sm font-semibold px-4 py-2 rounded-md">View Dispatch</button>
                  <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/track/${dispatch.tracking_token}`); toast.success("Tracking link copied"); }} className="text-sm border border-slate-300 rounded-md px-4 py-2">Copy Tracking Link</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between mt-6 pt-4 border-t border-slate-100">
          <button disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="text-sm text-slate-600 flex items-center gap-1 disabled:opacity-40"><ArrowLeft size={14} /> Back</button>
          {step < STEPS.length - 1 && (
            <button data-testid="btn-wiz-next" disabled={!canNext} onClick={goNext} className="bg-[var(--navy)] hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-60">
              Continue <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>

      {abOpen && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-40 p-6" onClick={() => setAbOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-alignbooks">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Upload size={18} /> Pull Invoice from AlignBooks</h2>
            <div className="text-xs text-slate-500 mt-1">Pick an invoice — Invoice #, Amount incl. GST, Client, PO, Date and Branch will be prefilled into the form.</div>

            {!abConfigured && (
              <div className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
                AlignBooks is not yet configured. Ask your super admin to open <b>Settings → AlignBooks Integration</b> and paste the base URL + API key.
              </div>
            )}
            {abError && (
              <div className="mt-4 p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-900">
                {abError}
              </div>
            )}
            {abLoading && <div className="mt-4 text-sm text-slate-500">Fetching invoices from AlignBooks…</div>}
            {!abLoading && abConfigured && !abError && abInvoices.length === 0 && (
              <div className="mt-4 text-sm text-slate-500">No invoices returned by AlignBooks.</div>
            )}
            {abInvoices.length > 0 && (
              <div className="mt-4 border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">Invoice #</th>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-right px-3 py-2">Amount</th>
                      <th className="text-left px-3 py-2">Client</th>
                      <th className="text-left px-3 py-2">PO</th>
                      <th className="text-left px-3 py-2">Branch</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {abInvoices.map((inv, i) => (
                      <tr key={inv.external_id || i} data-testid={`ab-inv-row-${inv.invoice_number || i}`} className="border-t border-slate-100 hover:bg-sky-50">
                        <td className="px-3 py-2 font-mono">{inv.invoice_number}</td>
                        <td className="px-3 py-2">{(inv.invoice_date || "").slice(0, 10)}</td>
                        <td className="px-3 py-2 text-right font-semibold">₹{Number(inv.invoice_amount || 0).toLocaleString("en-IN")}</td>
                        <td className="px-3 py-2">{inv.client_name}</td>
                        <td className="px-3 py-2">{inv.client_po_number}</td>
                        <td className="px-3 py-2">{inv.branch}</td>
                        <td className="px-3 py-2 text-right">
                          {inv.already_imported ? (
                            <span className="text-xs text-slate-400">Already imported</span>
                          ) : (
                            <button data-testid={`btn-pick-ab-${inv.invoice_number || i}`} onClick={() => pickAlignbooksInvoice(inv)} className="text-xs font-semibold px-3 py-1 bg-[var(--navy)] text-white rounded-md">Use</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button onClick={() => setAbOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-md">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
